<template>
  <div ref="target" class="relative" @click="toggleShowDropdown">
    <InputGroup class="input-group">
      <Textarea
        :key="name"
        ref="textarea"
        v-model="model"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        :placeholder="placeholder"
        fluid
        class="auto-grow-input"
        :class="{ 'is-invalid': state === false, 'is-valid': state === true }"
        :style="{ '--predef-min-height': minHeight + 'px' }"
      />
      <InputGroupAddon>
        <span class="pi pi-arrow-down"></span>
      </InputGroupAddon>
    </InputGroup>
    <div :class="`dropdown-menu show ${menuClass}`" v-show="showDropdown">
      <div class="dropdown-items">
        <!-- User phrases section -->
        <template v-if="userPhrases.length">
          <div class="dropdown-section-header">
            {{ t('messages.myPhrases') }}
          </div>
          <div
            v-for="phrase in userPhrases"
            :key="`user-${phrase.id}`"
            class="dropdown-item group flex items-center justify-between"
            @click="selectOption({ label: phrase.label, value: phrase.id })"
          >
            <span class="mr-2 flex-1">{{ phrase.label }}</span>
            <button
              class="delete-phrase-btn opacity-0 group-hover:opacity-100"
              :title="t('messages.delete')"
              @click.stop="deletePhrase(phrase)"
            >
              <span class="pi pi-times text-xs text-red-500"></span>
            </button>
          </div>
          <div v-if="componentOptions.length" class="dropdown-divider"></div>
        </template>

        <!-- Predefined values section -->
        <template v-if="componentOptions.length">
          <div v-if="userPhrases.length" class="dropdown-section-header">
            {{ t('messages.predefinedValues') }}
          </div>
          <div
            v-for="{ option } in componentOptions"
            :key="option.value"
            @click="selectOption(option)"
            class="dropdown-item"
          >
            {{ option.label }}
          </div>
        </template>

        <!-- Empty state -->
        <div
          v-if="!userPhrases.length && !componentOptions.length"
          class="dropdown-empty-state"
        >
          {{ t('messages.noPhrasesYet') }}
        </div>
      </div>

      <!-- Save phrase footer -->
      <div v-if="model && model.trim()" class="dropdown-footer">
        <button
          class="save-phrase-btn"
          :disabled="savingPhrase"
          @click.stop="saveAsPhrase"
        >
          <span class="pi pi-bookmark mr-1"></span>
          {{ t('messages.saveAsPhrase') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onClickOutside } from '@vueuse/core'
import { computed, nextTick, onMounted, ref, shallowRef, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePredefinedValues } from '@/composables/usePredefinedValues'

const { t } = useI18n()

const target = useTemplateRef('target')
const textarea = ref(null)

const emit = defineEmits(['input', 'selected', 'loaded', 'blur'])

const props = defineProps({
  type: {
    type: String,
    default: 'text',
  },
  value: {
    type: String,
    required: false,
  },
  category: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  state: {
    type: Boolean,
    default: null,
  },
  menuClass: {
    type: String,
    default: '',
  },
  appendOnSelect: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Array,
    default: () => [],
  },
  minHeight: {
    type: Number,
    default: 42,
  },
})

const model = defineModel({
  default: '',
})

const pools = usePredefinedValues(
  () => props.category,
  () => props.name,
)
const { userPhrases, savingPhrase } = pools

// Keep the historical `{ option }` wrapper shape — the template destructures
// it and the `loaded` emit is part of the component's public contract.
const componentOptions = computed(() =>
  props.options.length ? props.options : pools.options.value.map(option => ({ option })),
)
const showDropdown = shallowRef(false)

onMounted(async () => {
  if (props.options.length == 0) {
    await pools.loadOptions()
  }
  emit('loaded', componentOptions.value)

  pools.loadUserPhrases()
})

const saveAsPhrase = () => pools.saveAsPhrase(model.value)

const deletePhrase = phrase => pools.deletePhrase(phrase)

const toggleShowDropdown = () => {
  showDropdown.value = !showDropdown.value
}

onClickOutside(target, event => clickOutHandler())

const clickOutHandler = () => {
  showDropdown.value = false
}

const selectOption = option => {
  if (props.appendOnSelect) {
    model.value = model.value ? model.value + ', ' + option.label : option.label
  } else {
    model.value = option.label
  }
  nextTick(() => {
    emit('selected', model.value)
  })
}

const handleInput = () => {
  nextTick(() => {
    adjustHeight()
  })
}

// The ?. guards: inside an unmounting container (e.g. the transactions
// Drawer) focus/blur can fire after the template ref is already nulled.
const handleFocus = () => {
  const textareaEl = textarea.value?.$el
  if (textareaEl && textareaEl.scrollHeight > props.minHeight) {
    textareaEl.style.height = 'auto'
    textareaEl.style.height = `${textareaEl.scrollHeight}px`
  }
}

const handleBlur = e => {
  const textareaEl = textarea.value?.$el
  if (textareaEl) {
    if (textareaEl.scrollHeight) {
      textareaEl.style.height = `${props.minHeight}px`
    }
  }
  emit('blur', e)
}

const adjustHeight = () => {
  const textareaEl = textarea.value?.$el
  if (textareaEl) {
    textareaEl.style.height = 'auto'
    const scrollHeight = textareaEl.scrollHeight

    if (scrollHeight <= props.minHeight && document.activeElement !== textareaEl) {
      textareaEl.style.height = `${props.minHeight}px`
    } else {
      textareaEl.style.height = `${scrollHeight}px`
    }
  }
}
</script>

<style scoped>
  @reference '../../css/tailwind.css';

  .auto-grow-input {
    min-height: var(--predef-min-height, 42px) !important;
    height: var(--predef-min-height, 42px);
    transition: height 0.15s ease-in-out;
    resize: none !important;
    overflow: hidden;
    line-height: 1.5;
    padding: 0.375rem 0.75rem !important;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;

    @apply dark:border-gray-600;

    &:focus {
      color: #495057;
      background-color: #fff;
      border-color: #80bdff;
      outline: 0;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      @apply dark:border-gray-600 dark:bg-black dark:text-white;
    }

    &.is-invalid {
      border-color: #dc3545;
      &:focus {
        box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
      }
    }

    &.is-valid {
      border-color: #28a745;
      &:focus {
        box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
      }
    }

    &::-webkit-scrollbar {
      display: none;
    }

    scrollbar-width: none;
  }

  .dropdown-menu {
    width: 100%;
    padding: 0;
    position: absolute;
    z-index: 1000;
    background-color: #fff;
    @apply rounded-md shadow-md dark:bg-gray-800 dark:text-white;
  }

  .dropdown-items {
    max-height: 300px;
    overflow-y: auto;
    padding: 0.5rem 0;

    .dropdown-item {
      white-space: normal;
      word-wrap: break-word;
      padding: 0.5rem 1rem;
      line-height: 1.4;
      cursor: pointer;

      &:hover {
        background-color: #f8f9fa;
        @apply dark:bg-gray-700;
      }
    }
  }

  .dropdown-section-header {
    padding: 0.25rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #6b7280;
    letter-spacing: 0.05em;
    @apply dark:text-gray-400;
  }

  .dropdown-divider {
    height: 1px;
    margin: 0.25rem 0;
    background-color: #e5e7eb;
    @apply dark:bg-gray-600;
  }

  .dropdown-empty-state {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    color: #6b7280;
    font-style: italic;
    @apply dark:text-gray-400;
  }

  .dropdown-footer {
    border-top: 1px solid #e5e7eb;
    padding: 0.5rem;
    @apply dark:border-gray-600;
  }

  .save-phrase-btn {
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    color: #3b82f6;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    border-radius: 0.25rem;
    display: flex;
    align-items: center;

    &:hover {
      background-color: #eff6ff;
      @apply dark:bg-gray-700;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .delete-phrase-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 0.25rem;
    transition: opacity 0.15s ease-in-out;

    &:hover {
      background-color: #fee2e2;
      @apply dark:bg-red-900/30;
    }
  }
</style>
